import { lazy, Suspense } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { ActiveClientProvider } from '@/contexts/ActiveClientContext';
import { NavigationProvider } from '@/contexts/NavigationContext';
import { ProfileProvider } from '@/contexts/ProfileContext';
import { ProtectedRoute } from './ProtectedRoute';
import { AdminRoute } from './AdminRoute';
import App from '@/App';
import LoginPage from '@/pages/LoginPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import NotFoundPage from '@/pages/NotFoundPage';

const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const VeiculosPage = lazy(() => import('@/pages/VeiculosPage'));
const VeiculoDetalhePage = lazy(() => import('@/pages/veiculo-detalhe/VeiculoDetalhePage'));
const MotoristasPage = lazy(() => import('@/pages/MotoristasPage'));
const MotoristaDetalhePage = lazy(() => import('@/pages/motorista-detalhe/MotoristaDetalhePage'));
const CartoesPage = lazy(() => import('@/pages/CartoesPage'));
const MonitoramentoPage = lazy(() => import('@/pages/MonitoramentoPage'));
const AgendamentosPage = lazy(() => import('@/pages/AgendamentosPage'));
const FaturasPage = lazy(() => import('@/pages/FaturasPage'));
const RelatoriosPage = lazy(() => import('@/pages/RelatoriosPage'));
const RelatoriosBuilderPage = lazy(() => import('@/pages/relatorios/builder/BuilderPage'));
const RelatoriosVisualizacaoPage = lazy(
  () => import('@/pages/relatorios/VisualizacaoPage'),
);
const RelatoriosModeloRunnerPage = lazy(
  () => import('@/pages/relatorios/ModeloRunnerPage'),
);
const RedePage = lazy(() => import('@/pages/RedePage'));
const RedeRecolhaPage = lazy(() => import('@/pages/RedeRecolhaPage'));
const RecolhaPage = lazy(() => import('@/pages/RecolhaPage'));
const TerminalPage = lazy(() => import('@/pages/terminal/TerminalPage'));
const PostoDashboardPage = lazy(() => import('@/pages/PostoDashboardPage'));
const PostoRelatoriosPage = lazy(() => import('@/pages/PostoRelatoriosPage'));
const AuditoriaPage = lazy(() => import('@/pages/AuditoriaPage'));
const UsuariosPage = lazy(() => import('@/pages/UsuariosPage'));
const UsuarioDetalhePage = lazy(() => import('@/pages/usuario-detalhe/UsuarioDetalhePage'));
const GruposRbacPage = lazy(() => import('@/pages/grupos-rbac/GruposRbacPage'));
const MinhaContaPage = lazy(() => import('@/pages/minha-conta/MinhaContaPage'));
const AdminFluxosPage = lazy(() => import('@/pages/AdminFluxosPage'));
const DesignSystemPage = lazy(() => import('@/pages/DesignSystemPage'));
const ComponentsShowcase = lazy(() => import('@/pages/dev/ComponentsShowcase'));

function PageLoading() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="flex flex-col items-center gap-2 text-text-muted">
        <svg
          className="w-5 h-5 animate-spin text-primary"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        <span className="text-xs">Carregando...</span>
      </div>
    </div>
  );
}

function withSuspense(node: React.ReactNode) {
  return <Suspense fallback={<PageLoading />}>{node}</Suspense>;
}

function RootLayout() {
  return (
    <AuthProvider>
      <ActiveClientProvider>
        <NavigationProvider>
          <ProfileProvider>
            <Outlet />
          </ProfileProvider>
        </NavigationProvider>
      </ActiveClientProvider>
    </AuthProvider>
  );
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/dev/components', element: withSuspense(<ComponentsShowcase />) },
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: '/',
            element: <App />,
            children: [
              { index: true, element: withSuspense(<DashboardPage />) },
              { path: 'veiculos', element: withSuspense(<VeiculosPage />) },
              { path: 'veiculos/:uuid', element: withSuspense(<VeiculoDetalhePage />) },
              { path: 'motoristas', element: withSuspense(<MotoristasPage />) },
              { path: 'motoristas/:uuid', element: withSuspense(<MotoristaDetalhePage />) },
              { path: 'cartoes', element: withSuspense(<CartoesPage />) },
              { path: 'monitoramento', element: withSuspense(<MonitoramentoPage />) },
              { path: 'agendamentos', element: withSuspense(<AgendamentosPage />) },
              { path: 'faturas', element: withSuspense(<FaturasPage />) },
              { path: 'relatorios', element: withSuspense(<RelatoriosPage />) },
              {
                path: 'relatorios/builder',
                element: withSuspense(<RelatoriosBuilderPage />),
              },
              {
                path: 'relatorios/builder/:uuid',
                element: withSuspense(<RelatoriosBuilderPage />),
              },
              {
                path: 'relatorios/modelo/:entidade/:templateId',
                element: withSuspense(<RelatoriosModeloRunnerPage />),
              },
              {
                path: 'relatorios/:uuid',
                element: withSuspense(<RelatoriosVisualizacaoPage />),
              },
              { path: 'rede', element: withSuspense(<RedePage />) },
              { path: 'rede/recolha', element: withSuspense(<RedeRecolhaPage />) },
              { path: 'recolha', element: withSuspense(<RecolhaPage />) },
              { path: 'terminal', element: withSuspense(<TerminalPage />) },
              { path: 'posto', element: withSuspense(<PostoDashboardPage />) },
              { path: 'posto/relatorios', element: withSuspense(<PostoRelatoriosPage />) },
              { path: 'auditoria', element: withSuspense(<AuditoriaPage />) },
              { path: 'usuarios', element: withSuspense(<UsuariosPage />) },
              { path: 'usuarios/:uuid', element: withSuspense(<UsuarioDetalhePage />) },
              { path: 'minha-conta', element: withSuspense(<MinhaContaPage />) },
              {
                element: <AdminRoute />,
                children: [
                  { path: 'admin/grupos-rbac', element: withSuspense(<GruposRbacPage />) },
                  { path: 'admin/fluxos', element: withSuspense(<AdminFluxosPage />) },
                  { path: 'admin/design-system', element: withSuspense(<DesignSystemPage />) },
                ],
              },
              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
]);
